/**
 * DSL Parser with detailed diagnostics
 */

import {
  DSL_GRAMMAR,
  type DslFieldOperationDefinition,
  type DslObjectOperationDefinition,
} from '../dsl-grammar.js';
import type {
  DslDiagnostic,
  DslInstruction,
  DslParseResult,
  ParsedArgument,
  RuleOpts,
} from './ast.js';
import type { PositionedToken } from './lexer.js';
import { lexWithPos } from './lexer.js';

const FIELD_OPERATION_MAP: ReadonlyMap<string, DslFieldOperationDefinition> =
  new Map(DSL_GRAMMAR.fieldOperations.map((op) => [op.name, op]));

const OBJECT_OPERATION_MAP: ReadonlyMap<string, DslObjectOperationDefinition> =
  new Map(DSL_GRAMMAR.objectOperations.map((op) => [op.name, op]));

const DATE_OPERATION_SET: ReadonlySet<string> = new Set(
  DSL_GRAMMAR.dateOperations.map((op) => op.name),
);

/**
 * Parse DSL input with detailed diagnostic information
 */
export function parseDslDetailed(input: string): DslParseResult {
  const diagnostics: DslDiagnostic[] = [];
  const instructions: DslInstruction[] = [];
  const tokens = lexWithPos(input);
  let i = 0;

  function peek() {
    return tokens[i];
  }

  function consume(type?: string, value?: string) {
    const t = tokens[i];
    if (!t) {
      recordErr(lastToken() || null, 'Unexpected end of input');
      return undefined;
    }
    if (type && t.type !== type) {
      recordErr(t, `Expected ${type} got ${t.type}`);
      i++;
      return undefined;
    }
    if (value && t.value !== value) {
      recordErr(t, `Expected '${value}' got '${t.value}'`);
      i++;
      return undefined;
    }
    i++;
    return t;
  }

  function lastToken() {
    return tokens[Math.min(i, tokens.length - 1)];
  }

  function recordErr(tok: PositionedToken | null, message: string) {
    const start = tok
      ? { offset: tok.start, line: tok.line, column: tok.col }
      : { offset: input.length, line: 0, column: 0 };
    const end = tok
      ? {
          offset: tok.end,
          line: tok.line,
          column: tok.col + (tok.end - tok.start),
        }
      : start;
    diagnostics.push({
      message: `DSL parse error: ${message}`,
      severity: 'error',
      range: { start, end },
    });
  }

  for (const token of tokens) {
    if (token.error) recordErr(token, token.error);
  }

  function parseIdentifier(): string | undefined {
    const t = consume('identifier');
    return t?.value;
  }

  function parseArgument(): ParsedArgument | undefined {
    const t = tokens[i];
    if (!t) {
      recordErr(lastToken() || null, 'Expected value');
      return undefined;
    }
    if (t.type === 'string' || t.type === 'identifier' || t.type === 'number') {
      i++;
      return { value: t.value, tokenType: t.type };
    }
    recordErr(t, 'Expected string or identifier');
    i++;
    return undefined;
  }

  function parseOpts(): RuleOpts | undefined {
    const t0 = peek();
    if (t0 && t0.type === 'punct' && t0.value === '{') {
      consume('punct', '{');
      const opts: RuleOpts & Record<string, string> = {};
      let look = peek();

      while (look && !(look.type === 'punct' && look.value === '}')) {
        const key = parseIdentifier();
        consume('punct', ':');
        const valTok = tokens[i];
        if (!valTok) {
          recordErr(look, 'Expected option value');
          break;
        }
        if (valTok.type !== 'string' && valTok.type !== 'identifier') {
          recordErr(valTok, 'Option value must be string or identifier');
          i++;
          look = peek();
          continue;
        }
        i++;
        if (key) opts[key] = valTok.value;
        const comma = peek();
        if (comma && comma.type === 'punct' && comma.value === ',')
          consume('punct', ',');
        look = peek();
      }

      consume('punct', '}');
      return opts;
    }
    return undefined;
  }

  function parseStatement() {
    const first = consume('identifier');
    if (!first) return;

    if (first.value === 'field') {
      consume('punct', '(');
      const fieldArg = parseArgument();
      const fieldName = fieldArg?.value;
      consume('punct', ')');
      consume('punct', '.');
      const opTok = consume('identifier');
      consume('punct', '(');
      if (!opTok) {
        return;
      }

      const op = opTok.value;
      const def = FIELD_OPERATION_MAP.get(op);
      if (!def) {
        recordErr(opTok, `Unknown op ${op}`);
        while (true) {
          const next = peek();
          if (!next || (next.type === 'punct' && next.value === ')')) break;
          i++;
        }
        consume('punct', ')');
        return;
      }

      let parseError = false;
      const args: (ParsedArgument | undefined)[] = [];
      for (let index = 0; index < def.args.length; index++) {
        if (index > 0) consume('punct', ',');
        const value = parseArgument();
        if (value === undefined) parseError = true;
        else args.push(value);
      }

      let opts: RuleOpts | undefined;
      if (def.allowOptions ?? true) {
        const maybeComma = peek();
        if (
          maybeComma &&
          maybeComma.type === 'punct' &&
          maybeComma.value === ','
        ) {
          consume('punct', ',');
          opts = parseOpts();
        }
      }
      consume('punct', ')');

      if (!fieldName || parseError || args.length < def.args.length) {
        return;
      }

      switch (def.instruction.kind) {
        case 'cmp': {
          const [otherArg] = args;
          if (!otherArg) break;
          instructions.push({
            kind: 'cmp',
            op: def.instruction.op,
            field: fieldName,
            other: otherArg.value,
            otherType: otherArg.tokenType,
            opts,
          });
          break;
        }
        case 'within': {
          const [minField, maxField] = args;
          if (minField && maxField)
            instructions.push({
              kind: 'within',
              field: fieldName,
              minField: minField.value,
              maxField: maxField.value,
              minFieldType: minField.tokenType,
              maxFieldType: maxField.tokenType,
              opts,
            });
          break;
        }
        case 'withinHours': {
          const [other, hoursArg] = args;
          if (other && hoursArg) {
            const hours = Number(hoursArg.value);
            if (Number.isFinite(hours)) {
              instructions.push({
                kind: 'withinHours',
                field: fieldName,
                other: other.value,
                otherType: other.tokenType,
                hours,
                opts,
              });
            } else {
              recordErr(
                opTok,
                `withinHours expects a numeric hours argument, got '${hoursArg.value}'`,
              );
            }
          }
          break;
        }
        case 'withinMinutes': {
          const [other, minutesArg] = args;
          if (other && minutesArg) {
            const minutes = Number(minutesArg.value);
            if (Number.isFinite(minutes)) {
              instructions.push({
                kind: 'withinMinutes',
                field: fieldName,
                other: other.value,
                otherType: other.tokenType,
                minutes,
                opts,
              });
            } else {
              recordErr(
                opTok,
                `withinMinutes expects a numeric minutes argument, got '${minutesArg.value}'`,
              );
            }
          }
          break;
        }
        case 'requiredIf': {
          const [otherArg] = args;
          if (!otherArg) break;
          instructions.push({
            kind: 'requiredIf',
            field: fieldName,
            other: otherArg.value,
            otherType: otherArg.tokenType,
            opts,
          });
          break;
        }
        case 'requiredIfValue': {
          const [other, valueArg] = args;
          if (other && valueArg)
            instructions.push({
              kind: 'requiredIfValue',
              field: fieldName,
              other: other.value,
              otherType: other.tokenType,
              value: valueArg.value,
              valueType: valueArg.tokenType,
              opts,
            });
          break;
        }
        case 'notFutureDate': {
          instructions.push({ kind: 'notFutureDate', field: fieldName, opts });
          break;
        }
        case 'refine': {
          const [expr] = args;
          if (!expr) break;
          instructions.push({
            kind: 'refine',
            field: fieldName,
            expr: expr.value,
            opts,
          });
          break;
        }
      }
    } else if (first.value === 'object') {
      consume('punct', '.');
      const opTok = consume('identifier');
      consume('punct', '(');
      if (!opTok) {
        return;
      }

      const def = OBJECT_OPERATION_MAP.get(opTok.value);
      if (!def) {
        recordErr(opTok, `Unknown object op ${opTok.value}`);
        while (true) {
          const next = peek();
          if (!next || (next.type === 'punct' && next.value === ')')) break;
          i++;
        }
        consume('punct', ')');
        return;
      }

      let parseError = false;
      const args: (ParsedArgument | undefined)[] = [];
      for (let index = 0; index < def.args.length; index++) {
        if (index > 0) consume('punct', ',');
        const value = parseArgument();
        if (value === undefined) parseError = true;
        else args.push(value);
      }

      let opts: RuleOpts | undefined;
      if (def.allowOptions ?? true) {
        const maybe = peek();
        if (maybe && maybe.type === 'punct' && maybe.value === ',') {
          consume('punct', ',');
          opts = parseOpts();
        }
      }
      consume('punct', ')');

      if (parseError || args.length < def.args.length) {
        return;
      }

      if (def.instruction.kind === 'objectRefine') {
        const [code, expr] = args;
        if (code && expr)
          instructions.push({
            kind: 'objectRefine',
            code: code.value,
            expr: expr.value,
            opts,
          });
      }
    } else if (first.value === 'dates') {
      consume('punct', '(');
      const fields: string[] = [];
      while (true) {
        const f = parseArgument();
        if (f) fields.push(f.value);
        const p = peek();
        if (p && p.type === 'punct' && p.value === ',') {
          consume('punct', ',');
          continue;
        }
        break;
      }
      consume('punct', ')');
      consume('punct', '.');
      const opTok = consume('identifier');
      consume('punct', '(');

      if (opTok && !DATE_OPERATION_SET.has(opTok.value)) {
        recordErr(opTok, `dates() does not support ${opTok.value}`);
      }

      let opts: RuleOpts | undefined;
      const maybe = peek();
      if (maybe && maybe.type === 'punct' && maybe.value === ',') {
        consume('punct', ',');
        opts = parseOpts();
      } else {
        opts = parseOpts();
      }
      consume('punct', ')');

      if (opTok && DATE_OPERATION_SET.has(opTok.value)) {
        instructions.push({ kind: 'datesNotFuture', fields, opts });
      }
    } else {
      recordErr(first, `Unknown leading identifier ${first.value}`);
    }
  }

  while (peek()) {
    parseStatement();
    const semi = peek();
    if (semi && semi.type === 'punct' && semi.value === ';')
      consume('punct', ';');
    else if (semi && semi.type === 'punct' && semi.value !== ';') {
      i++;
    }
  }

  return { instructions, diagnostics };
}
